/**
 * aiApi.js — HTTP client for the AI Assistant backend (text_to_bpmn_api).
 *
 * Endpoints:
 *   POST /api/v1/assistant/transcribe        — audio blob → text
 *   POST /api/v1/assistant/extract-document  — PDF file → extracted text (BPMN wizard)
 *   POST /api/v1/assistant/extract           — PDF/DOCX/TXT → extracted text (DMN wizard)
 *   POST /api/v1/assistant/describe          — text + docs → typed outline
 *   POST /api/v1/assistant/generate-bpmn       — description / outline → BPMN XML
 *   POST /api/v1/assistant/dmn-outline         — text → Markdown DMN outline (LLM phase)
 *   POST /api/v1/assistant/dmn-outline-lint    — outline → issues (deterministic)
 *   POST /api/v1/assistant/dmn-outline-to-xml  — outline → DMN 1.3 XML (deterministic)
 *   POST /api/v1/assistant/dmn-outline-complete— outline + targets → surgical LLM patches
 *   POST /api/v1/assistant/assist              — general intents (validate, explain, modify, …)
 */

/**
 * Resolve AI backend base URL at runtime.
 *
 * Priority:
 *  1. AI_ASSISTANT_URL in .env (build-time) — set for external service, leave empty for dev
 *  2. window.__AI_ASSISTANT_URL__ — runtime override via index.html
 *  3. localhost / 127.0.0.1 / Coder subdomain → '' (relative, webpack proxy handles it)
 *  4. Standard IP / domain: same host, port 8000
 */
function resolveAiBase() {
  if (process.env.AI_ASSISTANT_URL) return process.env.AI_ASSISTANT_URL;
  if (typeof window !== 'undefined' && window.__AI_ASSISTANT_URL__) {
    return window.__AI_ASSISTANT_URL__;
  }
  if (typeof window === 'undefined') return '';

  const { protocol, hostname } = window.location;

  // localhost / 127.0.0.1 / Coder subdomain: relative URL → webpack proxy handles
  // routing inside the container, bypassing Coder's auth layer on exposed ports
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.match(/^\d+--/)  // Coder pattern: <port>--<workspace>.<domain>
  ) return '';

  // Standard IP / domain: same host, AI backend on port 8000
  return `${protocol}//${hostname}:8000`;
}

const BASE = resolveAiBase();

// ── Transcribe voice ──────────────────────────────────────────────────────────

/**
 * @param {Blob} audioBlob
 * @returns {Promise<string>}  Transcribed text
 */
export async function transcribe(audioBlob) {
  const extMap = {
    'audio/webm': 'recording.webm',
    'audio/ogg': 'recording.ogg',
    'audio/mp4': 'recording.mp4',
    'video/webm': 'recording.webm',
  };
  const filename = extMap[audioBlob.type] || 'recording.webm';
  const form = new FormData();
  form.append('file', audioBlob, filename);

  const res = await fetch(`${BASE}/api/v1/assistant/transcribe`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()).text;
}

// ── Extract document (PDF → text) ────────────────────────────────────────────

/**
 * Upload a PDF and get its extracted text back.
 * Backend handles size/type validation; this client just relays errors.
 *
 * @param {File} file
 * @returns {Promise<{ name: string, text: string, pages: number, char_count: number }>}
 */
export async function extractDocument(file) {
  const form = new FormData();
  form.append('file', file, file.name);

  const res = await fetch(`${BASE}/api/v1/assistant/extract-document`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── Describe / structure text (step 1 of generate) ───────────────────────────

/**
 * @param {string} message
 * @param {string} [language]
 * @param {Array<{name: string, text: string}>} [documents]
 * @returns {Promise<{ description: string, steps: string[], domain: string, complexity: string, structured_description?: string, outline_warnings?: Array }>}
 */
export async function describe(message, language = 'es', documents = []) {
  const res = await fetch(`${BASE}/api/v1/assistant/describe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, language, documents }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── Suggest outline improvements (LLM-driven, advisory) ──────────────────────

/**
 * Ask the backend for *optional* improvement suggestions on the outline. These
 * are tips ("add an actor", "split this task", "clarify this branch") — they
 * never block BPMN generation. Each suggestion can be applied with one click
 * by replacing or inserting a single outline line.
 *
 * @param {string} structuredDescription
 * @param {string} [language]
 * @param {number} [maxSuggestions]
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ suggestions: Array<{kind:string, line:number, before:?string, after:?string, message:string, rationale:?string}>, success:boolean }>}
 */
export async function suggestOutline(structuredDescription, language = 'es', maxSuggestions = 6, signal) {
  const res = await fetch(`${BASE}/api/v1/assistant/suggest-outline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      structured_description: structuredDescription || '',
      language,
      max_suggestions: maxSuggestions,
    }),
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── Generate BPMN (step 2 of generate) ───────────────────────────────────────

/**
 * @param {string} description           Original user prose (kept for fallback / audit).
 * @param {string} [language]            'es' | 'en'
 * @param {AbortSignal} [signal]
 * @param {string|null} [structured_description]  NaLa2BPMN prose (Camino 2) or typed outline (legacy).
 * @param {boolean} [outlineAuthoritative]        Legacy flag — only honoured when input_format='outline'.
 * @param {'prose'|'outline'} [inputFormat]       'prose' routes the edited NaLa2BPMN prose through
 *                                                the full DSPy pipeline (skip_reformulation=true so
 *                                                user edits travel verbatim). 'outline' uses the
 *                                                deterministic outline_to_bpmn_xml fast-path.
 * @returns {Promise<{ bpmn_xml: string, message: string, metadata: object }>}
 */
export async function generateBpmn(
  description,
  language = 'es',
  signal,
  structured_description = null,
  outlineAuthoritative = true,
  inputFormat = 'outline',
) {
  const hasStructured = structured_description != null && String(structured_description).trim().length > 0;
  const body = {
    description,
    language,
    structured_description,
    input_format: hasStructured ? inputFormat : 'outline',
    // Legacy fast-path only kicks in when explicitly requested AND we're in outline mode.
    outline_authoritative: Boolean(
      hasStructured && inputFormat === 'outline' && outlineAuthoritative,
    ),
  };
  const res = await fetch(`${BASE}/api/v1/assistant/generate-bpmn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const respBody = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${respBody || res.statusText}`);
  }
  return res.json();
}

// ── General assist (validate, explain, modify, suggest, document, chat) ───────

/**
 * @param {object} payload  AssistRequest schema (THEMIS_DMN_PLAN §2.2 for DMN intents)
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>}  AssistResponse
 */
export async function assist(payload, signal) {
  const res = await fetch(`${BASE}/api/v1/assistant/assist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // WT-G — version header so the backend can route to a future
      // payload schema without breaking older clients.
      'X-Themis-API-Version': '1',
    },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── DMN wizard — generic document extraction (PDF/DOCX/TXT) ───────────────────

/**
 * Upload a document (PDF / DOCX / TXT) for the DMN wizard and get its
 * extracted text + a backend-issued ``document_id`` back.
 *
 * Distinct from ``extractDocument`` (PDF-only, BPMN wizard) — keep both:
 * existing BPMN code paths still target ``/extract-document`` and we don't
 * want to introduce a regression risk by switching them over.
 *
 * @param {File} file
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ document_id: string, text: string, pages: number, metadata: object }>}
 */
export async function extractDmnDocument(file, signal) {
  const form = new FormData();
  form.append('file', file, file.name);

  const res = await fetch(`${BASE}/api/v1/assistant/extract`, {
    method: 'POST',
    headers: { 'X-Themis-API-Version': '1' },
    body: form,
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json();
}

// ── DMN two-phase wizard (THEMIS_BACKEND_COMPLETION_PLAN B2/B3/B4/B5) ────────
//
// Replaces the legacy `/generate-dmn` flow. Three deterministic endpoints
// (lint / build / complete) plus one LLM endpoint (outline). The wizard FE
// orchestrates them: text → outline → user edits → lint → build → apply.

const _THEMIS_HEADERS = {
  'Content-Type': 'application/json',
  'X-Themis-API-Version': '1',
};

async function _post(path, body, signal) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: _THEMIS_HEADERS,
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    const respBody = await res.text().catch(() => '');
    let detail = respBody;
    try {
      const parsed = JSON.parse(respBody);
      if (parsed?.error?.message) detail = parsed.error.message;
    } catch (_) { /* not json */ }
    const err = new Error(`${res.status}: ${detail || res.statusText}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Phase 1 — LLM produces a Markdown DMN outline (NOT XML).
 *
 * @param {{
 *   source: 'text'|'document',
 *   payload: string,
 *   language?: 'es'|'en',
 *   include_scenarios?: boolean,
 *   document_id?: string|null,
 * }} payload
 * @returns {Promise<{
 *   outline_text: string,
 *   outline: object,
 *   issues: Array<{line: number, code: string, severity: string, message: string, suggestion?: string}>,
 *   message: string,
 *   metadata: object,
 * }>}
 */
export async function dmnOutline(payload, signal) {
  return _post('/api/v1/assistant/dmn-outline', {
    source: payload.source || 'text',
    payload: payload.payload || '',
    language: payload.language || 'es',
    include_scenarios: payload.include_scenarios !== false,
    document_id: payload.document_id || null,
  }, signal);
}

/**
 * Deterministic outline linter — call on every (debounced) edit.
 * No LLM, no network cost beyond the round trip.
 */
export async function dmnOutlineLint(outlineText, language = 'es', signal) {
  return _post('/api/v1/assistant/dmn-outline-lint', {
    outline_text: outlineText,
    language,
  }, signal);
}

/**
 * Phase 2 — deterministic build of DMN 1.3 XML.
 *
 * Returns ``{ dmn_xml, issues, metadata: { blocked: bool } }``. When
 * ``blocked`` is true the outline still has error-severity issues; the FE
 * should disable "Apply" until they're fixed.
 */
export async function dmnOutlineToXml(outlineText, language = 'es', signal) {
  return _post('/api/v1/assistant/dmn-outline-to-xml', {
    outline_text: outlineText,
    language,
  }, signal);
}

/**
 * Phase 3 — LLM proposes surgical patches to an existing outline (fill
 * empty FEEL, suggest hit policy, recommend missing rules, etc.).
 *
 * @param {{
 *   outline: object,
 *   targets: Array<{kind: string, decision_name: string, rule_index?: number, hint?: string}>,
 *   language?: 'es'|'en',
 * }} payload
 * @returns {Promise<{
 *   patches: Array<{op: string, path: string, value: any}>,
 *   issues: Array<object>,
 *   message: string,
 *   metadata: object,
 * }>}
 */
export async function dmnOutlineComplete(payload, signal) {
  return _post('/api/v1/assistant/dmn-outline-complete', {
    outline: payload.outline,
    targets: payload.targets,
    language: payload.language || 'es',
  }, signal);
}
